<template>
  <WithAsideCards
    :data="data?.fields?.asideCards?.filter(instanceOfAsideCard)"
    data-profile-metrics-v="3.2.3"
  >
    <div>
      <BaseHeading size="h4" as="h2" :class="$style.header">
        {{ data?.fields?.header }}
      </BaseHeading>
      <div v-if="accountId" :class="$style.contentContainer">
        <MarkedParser :md="data?.fields?.copy" :class="$style.copy" />

        <div v-if="computedStores?.length">
          <div v-for="(store, storeIndex) in computedStores" :key="store.id">
            <BaseHeading
              v-if="Number(storeIndex) > 0 && !hideStoreInfo"
              :class="$style.storeTitle"
              size="h4"
              as="h3"
            >
              {{ store.name }}
            </BaseHeading>
            <div v-if="!hideStoreInfo" :class="$style.storeInfo">
              <BaseHeading
                v-if="store.address"
                :class="$style.storeAddress"
                size="h6"
                as="h4"
              >
                {{ store.address }}
              </BaseHeading>
              <BaseHeading
                v-if="store.address"
                :class="$style.storeAddress"
                size="h6"
              >
                {{ localizedStrings['store-id'] || 'Store ID' }} {{ store.id }}
              </BaseHeading>
            </div>
            <div v-if="store.status !== 404" :class="$style.stats">
              <div
                v-for="stat in store.stats"
                :key="stat.id"
                :class="$style.stat"
              >
                <div>
                  <img
                    v-if="stat?.icon?.fields?.file?.url"
                    :src="stat?.icon?.fields?.file?.url"
                    :alt="stat?.icon?.fields?.title"
                  />
                </div>

                <div>
                  <MarkedParser
                    :md="stat.copy"
                    :class="[$style.copy, 'u-type-p3']"
                  />
                </div>
                <div
                  :class="[$style.value, stat.error ? $style.valueError : '']"
                >
                  {{ store[stat.id] || 0 }}
                </div>
              </div>

              <MarkedParser
                v-if="store.insufficientMetrics"
                :md="data?.fields?.disclaimer"
                :class="[$style.copy, 'u-type-p3']"
              />
            </div>
          </div>
        </div>

        <MarkedParser
          v-else
          :md="data?.fields?.noResults"
          :class="[$style.copy, 'u-type-p3']"
        />
      </div>
    </div>
  </WithAsideCards>
</template>

<script setup lang="ts">
import type { Asset } from 'contentful'
import type { TypeProfileMetricsWithoutUnresolvableLinksResponse } from '~/types/contentful'

const { localizedStrings } = useLocalizedStrings()
const userStore = useUserStore()
const { accountId, roles, rolesRetrieved } = storeToRefs(userStore)

const props = defineProps<{
  data: TypeProfileMetricsWithoutUnresolvableLinksResponse
  // Only show the store selected via the `s` query param (fallback to the first)
  currentStoreOnly?: boolean
  // Hide the per-store name/address, e.g. when ProfileHero already shows them
  hideStoreInfo?: boolean
}>()

const { locale } = useI18n()
const route = useRoute()

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const ufetch = $fetch as typeof $fetch<any>

const { data: profileMetricsData } = await useAsyncData(
  `profile-metrics-data-${route.params?.slug}-${locale.value}`,
  async () => {
    return await ufetch('/api/profile-metrics', {
      method: 'POST',
      body: { roles: roles?.value },
    })
  },
  {
    transform(input) {
      return {
        metrics: input,
        fetchedAt: useUnixTimestamp(),
      }
    },
    getCachedData: (key) => useCachedData(key),
    watch: [() => locale.value],
  },
)

const computedMetricData = computed<
  {
    id:
      | 'totalTickets'
      | 'engagedPlayers'
      | 'activatedPlayers'
      | 'averageWeeklyPromoPacks'
    icon: Asset<'WITHOUT_UNRESOLVABLE_LINKS', string> | undefined
    copy: string
    metric: number
    premiumMetric: number
  }[]
>(() => {
  return [
    {
      id: 'totalTickets',
      icon: props.data?.fields?.metricTicketsIcon,
      copy: props.data?.fields?.metricTicketsCopy || '',
      metric: props.data?.fields?.metricTicketsMinimum || 0,
      premiumMetric: props.data?.fields?.premiumMetricTicketsMinimum || 0,
    },
    {
      id: 'engagedPlayers',
      icon: props.data?.fields?.metricEngagedPlayersIcon,
      copy: props.data?.fields?.metricEngagedPlayersCopy || '',
      metric: props.data?.fields?.metricEngagedPlayersMinimum || 0,
      premiumMetric:
        props.data?.fields?.premiumMetricEngagedPlayersMinimum || 0,
    },
    {
      id: 'activatedPlayers',
      icon: props.data?.fields?.metricActivePlayersIcon,
      copy: props.data?.fields?.metricActivePlayersCopy || '',
      metric: props.data?.fields?.metricActivePlayersMinimum || 0,
      premiumMetric: props.data?.fields?.premiumMetricActivePlayersMinimum || 0,
    },
    {
      id: 'averageWeeklyPromoPacks',
      icon: props.data?.fields?.metricAverageWeeklyPromoPacksIcon,
      copy: props.data?.fields?.metricAverageWeeklyPromoPacksCopy || '',
      metric: 0,
      premiumMetric: 0,
    },
  ]
})

interface Store {
  id: string
  isPremium?: boolean
  // dynamic keys for stats (e.g. store[stat.id] -> number)
  [key: string]: unknown
}

interface StoreWithStats extends Store {
  stats: {
    id: string
    icon: string
    copy: string
    error: boolean
    metric: number
  }[]
  insufficientMetrics: boolean
}

const computedStores = computed(() => {
  const stores = profileMetricsData.value?.metrics

  if (!stores || !rolesRetrieved.value) {
    return []
  }

  const mappedStores = stores
    .map((store: Store) => ({
      ...store,
      stats: computedMetricData.value?.map?.((stat) => {
        const metric = store?.isPremium ? stat.premiumMetric : stat.metric
        const storeStat = Number(store?.[stat?.id] ?? 0)

        return {
          id: stat.id,
          icon: stat.icon,
          copy: stat.copy,
          error: storeStat < metric,
          metric,
        }
      }),
    }))
    .map((store: StoreWithStats) => ({
      ...store,
      insufficientMetrics: store.stats.map(({ error }) => error).includes(true),
    }))

  if (props.currentStoreOnly) {
    const selectedStoreId = arrayAsString(route.query.s)
    const currentStore = mappedStores.find(
      (store: StoreWithStats) => store.id === selectedStoreId,
    )
    return currentStore ? [currentStore] : mappedStores.slice(0, 1)
  }

  return mappedStores
})
</script>

<style lang="scss" module>
.contentContainer {
  display: block;
}

.header {
  margin-bottom: 10px;
  font-weight: 700;
  font-size: 32px;
  line-height: 1.2;
  margin-bottom: 12px;
}

.copy {
  margin-bottom: 24px;
}

.stats {
  display: grid;
  gap: 20px;
  margin-bottom: 40px;
}

.stat {
  display: grid;
  align-items: center;
  justify-content: center;
  padding: 21px;
  gap: 15px;
  background-color: var(--white);
  border: 1px solid rgba(0, 0, 0, 0.04);
  box-shadow: 0 4px 13px 0 rgba(0, 0, 0, 0.06);
  text-align: center;

  @include media('sm') {
    grid-template-columns: 180px 1fr 180px;
    text-align: left;
  }
}

.value {
  align-self: center;
  font-weight: 700;
  font-size: to-rem(48px);
  line-height: 1.2;
  color: #504e8e;
  text-align: center;
}

.valueError {
  color: red;
}

.title {
  --type-h3-font-size-max: 38px;
}

.storeTitle {
  margin-bottom: 10px;
}

.storeInfo {
  margin-bottom: 20px;

  @include media('sm') {
    display: flex;
    justify-content: space-between;
    gap: 20px;
  }
}

.storeAddress {
  color: var(--gray-7);
}

.copy {
  color: #384860;

  h1,
  h2,
  h3,
  h4 {
    font-family: var(--roboto-condensed);
    color: #121a26;
    font-size: clamp(
      #{to-rem(32px)},
      #{math.div(38px, 1500px) * 100vw},
      #{to-rem(38px)}
    );
    line-height: 1.2;
  }
}
</style>
